import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import  moment from 'moment';
import { CommonService } from 'src/app/services/Common-function/common.service';

export type CellType = 'text' | 'number' | 'currency' | 'percent' | 'avatar' | 'status' | 'date' | 'toggle';

// Optional info popup shown when an "i" icon next to a column header is clicked.
export interface ColumnInfo {
  message: string;   // dialog body (HTML allowed)
  title?: string;    // defaults to the column header
  icon?: string;     // dialog icon (default 'pi pi-info-circle')
}

export interface ColumnDef {
  field: string;                                   // dot path: 'beat.title' or 'impressions'
  header: string;
  type?: CellType;                                 // default 'text'
  strong?: boolean;                                // bold prominent column
  hideOnDesktop?: boolean;                         // keep out of the table, still in cards/CSV
  exportable?: boolean;                            // default true
  imageField?: string;                             // avatar: image dot path
  subFormatter?: (row: any) => string;            // avatar: subtitle text
  statusFn?: (row: any) => { label: string; danger: boolean }; // status badge
  formatter?: (value: any, row: any) => string;   // custom cell text
  info?: ColumnInfo;                               // header "i" icon → info dialog
  sortable?: boolean;                              // per-column override of the table's [sortable]
  // type 'toggle': on/off state + whether the switch is locked for this row.
  toggleFn?: (row: any) => boolean;
  toggleDisabledFn?: (row: any) => boolean;
  toggleLabelFn?: (row: any) => string;            // optional text beside the switch
  // Small icon button rendered next to the cell value (e.g. edit daily budget).
  cellAction?: {
    icon: string;
    tooltip?: string;
    onClick: (row: any) => void;
    hiddenFn?: (row: any) => boolean;
  };
}

export interface RowAction {
  icon: string;            // PrimeIcon class, e.g. 'pi pi-pencil'
  label?: string;          // shown in desktop menu
  tooltip?: string;
  danger?: boolean;
  hiddenFn?: (row: any) => boolean;  // hide this action for specific rows
  onClick: (row: any) => void;
}
@Component({
  selector: 'ipm-common-data-table',
  templateUrl: './common-data-table.component.html',
  styleUrls: ['./common-data-table.component.scss']
})
export class CommonDataTableComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() columns: ColumnDef[] = [];
  @Input() data: any[] = [];
  @Input() rowActions: RowAction[] = [];
  @Input() showExport = true;
  @Input() exportFilename = 'export';
  @Input() locale = 'en-US';
  @Input() currencyCode = 'INR';
  @Input() percentDigits = 2;
  @Input() showActions = true;
  @Input() mobileView = false;
  // Column sorting is opt-in, so existing tables keep their original behaviour.
  // A column can override this either way with ColumnDef.sortable.
  @Input() sortable = false;
  // Render rowActions inside the first column (under its value) and reveal them
  // on row hover, instead of the trailing "Actions" menu column.
  @Input() inlineRowActions = false;
  // Height of the internal scroll area for the desktop table.
  @Input() scrollHeight = '400px';
  // When true, watch the internal scroll area and emit `scrollEnd` as it nears
  // the bottom, so the host can lazy-load the next page while keeping the
  // sticky header / frozen first column that scrollable mode provides.
  @Input() paginateOnScroll = false;
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowSelect = new EventEmitter<any>();
  // Emitted when a 'toggle' cell is switched.
  @Output() toggleChange = new EventEmitter<{ row: any; checked: boolean; column: ColumnDef }>();
  // Emitted once each time the internal scroll reaches near the bottom.
  @Output() scrollEnd = new EventEmitter<void>();
  @ViewChild('infoPop', { static: true }) infoPopTemplate!: TemplateRef<any>;

  // Scroll-pagination plumbing for the p-table's internal scroll container.
  private scrollUnlisten?: () => void;
  private atBottom = false;

  constructor(
    private commonService: CommonService,
    private host: ElementRef,
    private renderer: Renderer2,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (this.paginateOnScroll && !this.mobileView) {
      // p-table builds .p-datatable-wrapper after the view initialises.
      setTimeout(() => this.bindScrollPagination(), 0);
    }
  }

  ngOnDestroy(): void {
    if (this.scrollUnlisten) {
      this.scrollUnlisten();
    }
  }

  private bindScrollPagination(): void {
    const wrapper = this.host.nativeElement.querySelector('.p-datatable-wrapper') as HTMLElement | null;
    if (!wrapper) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.scrollUnlisten = this.renderer.listen(wrapper, 'scroll', () => this.onWrapperScroll(wrapper));
    });
  }

  // Emits once per approach to the bottom; re-arms after the user scrolls back up
  // so each new page only triggers a single fetch.
  private onWrapperScroll(wrapper: HTMLElement): void {
    const threshold = 120;
    const reachedBottom = wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - threshold;
    if (reachedBottom && !this.atBottom) {
      this.atBottom = true;
      this.zone.run(() => this.scrollEnd.emit());
    } else if (!reachedBottom && this.atBottom) {
      this.atBottom = false;
    }
  }

  private palette = [
    '#e8590c', '#3b5bdb', '#0ca678', '#ae3ec9',
    '#1098ad', '#f08c00', '#7048e8', '#c2255c',
  ];
 
  private expanded = new Set<any>();
  private imgErrors = new WeakSet<object>();
 
  menuModel: MenuItem[] = [];
 
  desktopColumns: ColumnDef[] = [];
  cardColumns: ColumnDef[] = [];
  avatarColumn?: ColumnDef;
  statusColumn?: ColumnDef;
  toggleColumn?: ColumnDef;
 
  ngOnChanges(): void {
    // A 'toggle' column doesn't get a column of its own — it renders inside the
    // first cell, ahead of that cell's value (see toggleColumn in the template).
    const visible = this.columns.filter((c) => !c.hideOnDesktop && c.type !== 'toggle');
    // render the status column immediately after the avatar/identity column
    const statusCol = visible.find((c) => c.type === 'status');
    if (statusCol) {
      const ordered = visible.filter((c) => c !== statusCol);
      const avatarIdx = ordered.findIndex((c) => c.type === 'avatar');
      if (avatarIdx !== -1) {
        ordered.splice(avatarIdx + 1, 0, statusCol);
        this.desktopColumns = ordered;
      } else {
        this.desktopColumns = visible;
      }
    } else {
      this.desktopColumns = visible;
    }
    this.cardColumns = this.columns.filter(
      (c) => c.type !== 'avatar' && c.type !== 'status' && c.type !== 'toggle'
    );
    this.avatarColumn = this.columns.find((c) => c.type === 'avatar');
    this.statusColumn = this.columns.find((c) => c.type === 'status');
    this.toggleColumn = this.columns.find((c) => c.type === 'toggle');
    this.expanded.clear();
    if (this.data?.length) {
      this.expanded.add(this.data[0]); // open the first card by default
    }
  }
 
  // ----- value helpers -----
  resolve(row: any, path: string): any {
    return path?.split('.').reduce((a, k) => (a == null ? a : a[k]), row);
  }
 
  isNumeric(col: ColumnDef): boolean {
    return col.type === 'number' || col.type === 'currency' || col.type === 'percent';
  }
 
  format(col: ColumnDef, row: any): string {
    if (col.formatter) {
      return col.formatter(this.resolve(row, col.field), row);
    }
    const v = this.resolve(row, col.field);
    switch (col.type) {
      case 'currency':
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
        }).format(Number(v) || 0);
      case 'percent':
        return (Number(v) || 0).toFixed(this.percentDigits) + '%';
      case 'number':
        return new Intl.NumberFormat(this.locale).format(Number(v) || 0);
      case 'date':
       // Guard against empty dates — moment(undefined) would render as today.
       return v ? moment(v).format('ll') : ''
      default:
        return v == null ? '' : String(v);
    }
  }
 
  status(col: ColumnDef | undefined, row: any): { label: string; danger: boolean } {
    return col?.statusFn ? col.statusFn(row) : { label: '', danger: false };
  }

  // ----- toggle cell -----
  toggleState(col: ColumnDef, row: any): boolean {
    return col?.toggleFn ? !!col.toggleFn(row) : false;
  }

  toggleDisabled(col: ColumnDef, row: any): boolean {
    return col?.toggleDisabledFn ? !!col.toggleDisabledFn(row) : false;
  }

  toggleLabel(col: ColumnDef, row: any): string {
    return col?.toggleLabelFn ? col.toggleLabelFn(row) : '';
  }

  onToggle(col: ColumnDef, row: any, checked: boolean): void {
    this.toggleChange.emit({ row, checked, column: col });
  }

  /** Whether this column's header can be clicked to sort. */
  isSortable(col: ColumnDef): boolean {
    if (!col?.field) {
      return false;
    }
    return col.sortable !== undefined ? col.sortable : this.sortable;
  }

  /** Row actions applicable to this row (some are status-dependent). */
  visibleActions(row: any): RowAction[] {
    return (this.rowActions || []).filter((a) => (a.hiddenFn ? !a.hiddenFn(row) : true));
  }

  /** Whether a column's inline action icon should render for this row. */
  showCellAction(col: ColumnDef, row: any): boolean {
    if (!col?.cellAction) {
      return false;
    }
    return col.cellAction.hiddenFn ? !col.cellAction.hiddenFn(row) : true;
  }

  /**
   * Maps a status label to a colour class for the badge. A `danger` flag from
   * statusFn falls back to the failed styling when the label is unrecognised.
   */
  statusClass(col: ColumnDef | undefined, row: any): string {
    const { label, danger } = this.status(col, row);
    const value = (label || '').toString().toLowerCase().trim();
    if (['running', 'live'].includes(value)) {
      return 'running';
    }
    if (value === 'completed') {
      return 'completed';
    }
    if (['active', 'success', 'paid'].includes(value)) {
      return 'active';
    }
    if (['pending', 'processing', 'in progress', 'in_progress', 'scheduled'].includes(value)) {
      return 'pending';
    }
    if (value === 'paused') {
      return 'paused';
    }
    if (['failed', 'fail', 'cancel', 'cancelled', 'canceled', 'stopped', 'declined', 'error', 'rejected'].includes(value)) {
      return 'failed';
    }
    return danger ? 'failed' : 'default';
  }
 
  // ----- avatar helpers (image from cover_picture, fallback to initial) -----
  avatarImage(row: any): string {
    return this.avatarColumn ? this.resolve(row, this.avatarColumn.imageField || '') : '';
  }
  avatarTitle(row: any): string {
    return this.avatarColumn ? this.resolve(row, this.avatarColumn.field) : '';
  }
  avatarSub(row: any): string {
    return this.avatarColumn?.subFormatter ? this.avatarColumn.subFormatter(row) : '';
  }
  initial(row: any): string {
    const t = String(this.avatarTitle(row) ?? '?');
    return (t[0] || '?').toUpperCase();
  }
  colorFor(i: number): string {
    return this.palette[i % this.palette.length];
  }
 
  onImgError(row: any): void {
    this.imgErrors.add(row);
  }
  hasImgError(row: any): boolean {
    return this.imgErrors.has(row);
  }
 
  // ----- mobile accordion -----
  toggle(row: any): void {
    this.expanded.has(row) ? this.expanded.delete(row) : this.expanded.add(row);
  }
  isExpanded(row: any): boolean {
    return this.expanded.has(row);
  }
 
  // ----- desktop actions menu -----
  openMenu(event: Event, row: any, menu: any): void {
    this.menuModel = this.visibleActions(row).map((a) => ({
      label: a.label ?? a.tooltip ?? '',
      icon: a.icon,
      styleClass: a.danger ? 'danger-item' : '',
      command: () => a.onClick(row),
    }));
    menu.toggle(event);
  }
 
  // ----- header info dialog -----
  // Opens the shared info dialog (centred on desktop, bottom sheet on mobile).
  openInfoDialog(col: ColumnDef, event: Event): void {
    event.stopPropagation();
    if (!col.info) {
      return;
    }
    const data = {
      title: col.info.title ?? col.header,
      message: col.info.message,
      icon: col.info.icon ?? 'pi pi-info-circle',
    };
    const className = this.commonService.getClassName('ipm-info-dialog-model');
    this.commonService.openModel(this.infoPopTemplate, data, className, false);
  }

  // ----- row click -----
  onRowClick(row: any): void {
    this.rowClick.emit(this.resolve(row, '_id'));
    this.rowSelect.emit(row);
  }

  trackByIndex = (i: number): number => i;

  // Track rows by their _id so updating one row (e.g. a status toggle) doesn't
  // re-render every other row; falls back to index when a row has no _id.
  rowTrackBy = (index: number, row: any): any => (row && row._id != null ? row._id : index);
 
  // ----- CSV export -----
  exportCsv(): void {
    const cols = this.columns.filter((c) => c.exportable !== false);
    const headers = cols.map((c) => c.header);
    const rows = this.data.map((row) =>
      cols.map((c) =>
        c.type === 'status' ? this.status(c, row).label : this.resolve(row, c.field)
      )
    );
    const esc = (v: any): string => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.exportFilename}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
